import { MessageSquareText, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DiscordGuildHealthLine } from './DiscordHealthLine';
import type { DiscordGuildSummary } from '@/Config/api/queryClient';
import { Button, IconButton } from '@/Domain/components/Shared/Button/Button';
import { Input } from '@/Domain/components/Shared/Input/Input';
import { Label } from '@/Domain/components/Shared/Label/Label';
import { Switch } from '@/Domain/components/Shared/Switch/Switch';

export interface DiscordServerRowProps {
  guild: DiscordGuildSummary;
  onToggleEnabled: (guildId: string, enabled: boolean) => void;
  onRemove: (guild: DiscordGuildSummary) => void;
  onSaveNickname: (guildId: string, nickname: string) => Promise<string | undefined>;
  onConfigureAutomatedMessages: (guild: DiscordGuildSummary) => void;
  /** Disables the row's controls while one of its own mutations is in flight. */
  disabled?: boolean;
  /** Live Discord verdict: undefined while loading, null when Discord was unreachable. */
  present?: boolean | null;
  healthLoading?: boolean;
}

/** One connected Discord server: name, health, enable switch, remove action, and the bot's display name there. */
export const DiscordServerRow = ({
  guild,
  onToggleEnabled,
  onRemove,
  onSaveNickname,
  onConfigureAutomatedMessages,
  disabled,
  present,
  healthLoading,
}: DiscordServerRowProps) => {
  const { t } = useTranslation(['integration']);
  const supportsNickname = guild.nickname !== undefined && guild.suggested_nickname !== undefined;
  const liveNickname = guild.nickname ?? '';
  const initialNickname = liveNickname || guild.suggested_nickname || '';
  const [nickname, setNickname] = useState(initialNickname);
  const previousLiveNickname = useRef(liveNickname);

  useEffect(() => {
    if (previousLiveNickname.current !== liveNickname) {
      setNickname(liveNickname);
      previousLiveNickname.current = liveNickname;
    }
  }, [liveNickname]);

  const trimmed = nickname.trim();
  const canApply = !disabled && (trimmed !== '' || liveNickname !== '');
  const saveNickname = async () => {
    const acceptedNickname = await onSaveNickname(guild.guild_id, trimmed);
    if (acceptedNickname !== undefined) {
      setNickname(acceptedNickname);
    }
  };

  return (
    <div
      className="flex flex-col gap-3 rounded-xl border border-border bg-card p-4"
      data-testid={`discord-server-row-${guild.guild_id}`}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p
            className="truncate font-medium text-foreground"
            data-testid={`discord-server-name-${guild.guild_id}`}
          >
            {guild.guild_name}
          </p>
          <DiscordGuildHealthLine
            guildId={guild.guild_id}
            enabled={guild.enabled}
            state={guild.state}
            present={present}
            loading={Boolean(healthLoading)}
          />
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Switch
            id={`discord-server-${guild.guild_id}-enabled`}
            checked={guild.enabled}
            onCheckedChange={(checked) => onToggleEnabled(guild.guild_id, checked)}
            disabled={disabled}
            aria-label={t('integration:discord.oneclick.enable_label', {
              server: guild.guild_name,
            })}
            data-testid={`discord-server-toggle-${guild.guild_id}`}
          />
          <IconButton
            variant="ghost"
            size="sm"
            onClick={() => onRemove(guild)}
            disabled={disabled}
            aria-label={t('integration:discord.oneclick.remove')}
            data-testid={`discord-server-remove-${guild.guild_id}`}
          >
            <Trash2 />
          </IconButton>
        </div>
      </div>

      {supportsNickname && (
        <div className="space-y-3">
          <Label htmlFor={`discord-server-${guild.guild_id}-nickname`}>
            {t('integration:discord.oneclick.nickname_label')}
          </Label>
          <div className="flex items-center gap-2">
            <Input
              id={`discord-server-${guild.guild_id}-nickname`}
              value={nickname}
              maxLength={32}
              autoComplete="off"
              placeholder={t('integration:discord.oneclick.nickname_placeholder')}
              disabled={disabled}
              className="w-full bg-card"
              onChange={(e) => setNickname(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && canApply) {
                  e.preventDefault();
                  void saveNickname();
                }
              }}
              data-testid={`discord-server-nickname-${guild.guild_id}`}
            />
            <Button
              variant="outline"
              size="sm"
              disabled={!canApply}
              onClick={() => void saveNickname()}
              data-testid={`discord-server-nickname-apply-${guild.guild_id}`}
            >
              {t('integration:discord.oneclick.nickname_apply')}
            </Button>
          </div>
        </div>
      )}

      {guild.state === 'ACTIVE' && guild.automated_messages && (
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => onConfigureAutomatedMessages(guild)}
          disabled={disabled}
          data-testid={`discord-server-automated-messages-${guild.guild_id}`}
        >
          <MessageSquareText data-icon="inline-start" />
          {t('integration:discord.oneclick.automated_messages.open')}
        </Button>
      )}
    </div>
  );
};

export default DiscordServerRow;
